import { Router, Request, Response } from 'express'
import { EmailAuthService } from './service'
import { asyncHandler } from '../frameworks/error-handler'
import { ValidationError } from '../errors'

/**
 * Create email authentication routes
 */
export function createEmailRoutes(emailAuthService: EmailAuthService): Router {
  const router = Router()

  // ============ Email Signup & Verification ============

  /**
   * POST /auth/email/signup
   * Sign up with email and password
   */
  router.post(
    '/auth/email/signup',
    asyncHandler(async (req: Request, res: Response) => {
      const { email, password, name } = req.body

      if (!email || typeof email !== 'string') {
        throw new ValidationError('Email must be a non-empty string')
      }

      if (!password || typeof password !== 'string') {
        throw new ValidationError('Password must be a non-empty string')
      }

      const result = await emailAuthService.signupWithEmail(email, password, name)

      res.status(201).json({
        ...result,
        requiresVerification: true
      })
    })
  )

  /**
   * POST /auth/email/verify
   * Verify email with code
   */
  router.post(
    '/auth/email/verify',
    asyncHandler(async (req: Request, res: Response) => {
      const { email, code } = req.body

      if (!email || typeof email !== 'string') {
        throw new ValidationError('Email must be a non-empty string')
      }

      if (!code || typeof code !== 'string') {
        throw new ValidationError('Code must be a non-empty string')
      }

      const result = await emailAuthService.verifyEmail(email, code, 'verify_email')

      if (result.verified && result.userId) {
        await emailAuthService.markEmailAsVerified(email)
      }

      res.json(result)
    })
  )

  /**
   * POST /auth/email/resend-code
   * Resend verification code
   */
  router.post(
    '/auth/email/resend-code',
    asyncHandler(async (req: Request, res: Response) => {
      const { email } = req.body

      if (!email || typeof email !== 'string') {
        throw new ValidationError('Email must be a non-empty string')
      }

      const result = await emailAuthService.startEmailVerification({
        email,
        type: 'verify_email'
      })

      res.json(result)
    })
  )

  // ============ Email Login ============

  /**
   * POST /auth/email/login
   * Login with email and password
   */
  router.post(
    '/auth/email/login',
    asyncHandler(async (req: Request, res: Response) => {
      const { email, password } = req.body

      if (!email || typeof email !== 'string') {
        throw new ValidationError('Email must be a non-empty string')
      }

      if (!password || typeof password !== 'string') {
        throw new ValidationError('Password must be a non-empty string')
      }

      const { session, user } = await emailAuthService.loginWithPassword(email, password)

      res.json({
        session,
        user,
        authenticated: true
      })
    })
  )

  // ============ Passwordless Login ============

  /**
   * POST /auth/email/send-login-link
   * Send passwordless login link
   */
  router.post(
    '/auth/email/send-login-link',
    asyncHandler(async (req: Request, res: Response) => {
      const { email, redirectUrl } = req.body

      if (!email || typeof email !== 'string') {
        throw new ValidationError('Email must be a non-empty string')
      }

      const result = await emailAuthService.sendLoginLink(email, redirectUrl)

      res.json(result)
    })
  )

  /**
   * POST /auth/email/verify-login-link
   * Complete passwordless login with code
   */
  router.post(
    '/auth/email/verify-login-link',
    asyncHandler(async (req: Request, res: Response) => {
      const { email, code } = req.body

      if (!email || typeof email !== 'string') {
        throw new ValidationError('Email must be a non-empty string')
      }

      if (!code || typeof code !== 'string') {
        throw new ValidationError('Code must be a non-empty string')
      }

      const result = await emailAuthService.completeLoginLink(email, code)

      res.json(result)
    })
  )

  // ============ Password Reset ============

  /**
   * POST /auth/email/forgot-password
   * Request password reset
   */
  router.post(
    '/auth/email/forgot-password',
    asyncHandler(async (req: Request, res: Response) => {
      const { email } = req.body

      if (!email || typeof email !== 'string') {
        throw new ValidationError('Email must be a non-empty string')
      }

      const result = await emailAuthService.requestPasswordReset(email)

      res.json(result)
    })
  )

  /**
   * POST /auth/email/reset-password
   * Reset password with code
   */
  router.post(
    '/auth/email/reset-password',
    asyncHandler(async (req: Request, res: Response) => {
      const { email, code, newPassword } = req.body

      if (!email || typeof email !== 'string') {
        throw new ValidationError('Email must be a non-empty string')
      }

      if (!code || typeof code !== 'string') {
        throw new ValidationError('Code must be a non-empty string')
      }

      if (!newPassword || typeof newPassword !== 'string') {
        throw new ValidationError('New password must be a non-empty string')
      }

      const result = await emailAuthService.resetPasswordWithCode(email, code, newPassword)

      res.json(result)
    })
  )

  // ============ Email Management ============

  /**
   * POST /auth/email/change-email
   * Request email change (requires authentication)
   */
  router.post(
    '/auth/email/change-email',
    asyncHandler(async (req: Request, res: Response) => {
      const { newEmail } = req.body

      // Get user ID from session or API key
      const userId = (req as any).apiKey?.principalId || (req as any).kitiumSession?.userId

      if (!userId) {
        throw new ValidationError('Authentication required')
      }

      if (!newEmail || typeof newEmail !== 'string') {
        throw new ValidationError('New email must be a non-empty string')
      }

      const result = await emailAuthService.requestEmailChange(userId, newEmail)

      res.json(result)
    })
  )

  /**
   * POST /auth/email/verify-email-change
   * Complete email change with verification code
   */
  router.post(
    '/auth/email/verify-email-change',
    asyncHandler(async (req: Request, res: Response) => {
      const { newEmail, code } = req.body

      // Get user ID from session or API key
      const userId = (req as any).apiKey?.principalId || (req as any).kitiumSession?.userId

      if (!userId) {
        throw new ValidationError('Authentication required')
      }

      if (!newEmail || typeof newEmail !== 'string') {
        throw new ValidationError('New email must be a non-empty string')
      }

      if (!code || typeof code !== 'string') {
        throw new ValidationError('Code must be a non-empty string')
      }

      const result = await emailAuthService.completeEmailChange(userId, newEmail, code)

      res.json(result)
    })
  )

  return router
}
